import _styled, { default as _styled2, default as _styled3 } from "styled-components";
import { Wrap } from "~/features/ui/wrap";
import { getThemeStyle, type Theme, type Mode } from "~/features/ui/get-theme-style";
import { takeFirst, hasItems } from "~/features/app/utils";
import { LinkBarsItem, type LinkBarsItemViewProps } from "~/features/blocks/inventory/link-bars/link-bars-item";
import { padNumber } from "~/features/app/utils";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type LinkBarsBlockViewProps<TItem = Omit<LinkBarsItemViewProps, "count" | "mode" | "theme">> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  getItemKey?: (item: TItem, index: number) => React.Key;
  hasTopDivider?: boolean | null;
  items?: TItem[] | null;
  mode?: string | null;
  renderItem: (item: TItem, index: number, count: string) => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  theme?: string | null;
};
export function LinkBarsBlockView<TItem = Omit<LinkBarsItemViewProps, "count" | "mode" | "theme">>({
  anchor,
  getItemKey,
  hasTopDivider = true,
  items = [],
  mode = "light",
  renderItem,
  sectionProps,
  theme = "white"
}: LinkBarsBlockViewProps<TItem>) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  const groups = items ?? [];
  if (!hasItems(groups)) {
    return null;
  }
  return (
    // Transparent section: the page pattern (set in CMS) shows through in the
    // margins around the framed block, incl. the open bands above and below the frame.
    <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[hasTopDivider && {
      "borderTopWidth": "1px"
    }, {
      "paddingTop": "var(--spacing-xl)",
      "paddingBottom": "var(--spacing-xl)",
      "@media (min-width: 1024px)": {
        "paddingTop": "var(--spacing-3xl)",
        "paddingBottom": "var(--spacing-3xl)"
      }
    }, themeStyle.text, themeStyle.border]}>
      {/* border-0: at max width the Wrap would draw side borders at the 1920 edge, which
          would double up with the inset frame's own border. */}
      <_StyledWrap mode={mode as Mode}>
        {/* relative z-10 lifts the white boxes above the absolutely-positioned page pattern.
            border-b closes the box itself: the bottom band means the next block's top
            divider is no longer adjacent to the frame.
            The desktop side margin (grid margin) keeps the page pattern visible next to the frame. */}
        <_StyledDiv $_css2={[{
          "position": "relative",
          "zIndex": "10",
          "> :not([hidden]) ~ :not([hidden])": {
            "--tw-divide-y-reverse": "0",
            "borderTopWidth": "calc(1px * calc(1 - var(--tw-divide-y-reverse)))",
            "borderBottomWidth": "calc(1px * var(--tw-divide-y-reverse))"
          },
          "borderWidth": "1px",
          "@media (min-width: 1024px)": {
            "marginLeft": "var(--grid-v3-margin)",
            "marginRight": "var(--grid-v3-margin)"
          }
        }, themeStyle.border]}>
          {groups.map((item, idx) => <div key={getItemKey?.(item, idx) || idx}>{renderItem(item, idx, padNumber(idx + 1))}</div>)}
        </_StyledDiv>
      </_StyledWrap>
    </_StyledSection>
  );
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-17oz3vt-0"
})`${p => p.$_css}`;
var _StyledWrap = _styled2(Wrap).withConfig({
  componentId: "sc-sf-17oz3vt-1"
})({
  "borderWidth": "0px"
});
var _StyledDiv = /*#__PURE__*/_styled3("div").withConfig({
  displayName: "link-bars___StyledDiv",
  componentId: "sc-1e6ulbw-0"
})(["", ""], p => p.$_css2);